import * as React from "react";
import * as monaco from 'monaco-editor/esm/vs/editor/editor.api';

import { ParameterEditorMode } from "../../../processEditor/components/ParameterInput";
import { languageProvider } from "../lang/languageProvider";
import useFontFaceObserver from "use-font-face-observer";

export const useEditor = (props: IUseEditorProps): string => {

    const getLanguage = (): string => {
        if (props.mode === undefined) {
            return "csharp";
        }
        switch (props.mode) {
            case ParameterEditorMode.csharp: {
                return "csharp";
            }
            case ParameterEditorMode.sql: {
                return "sql";
            }
            case ParameterEditorMode.json: {
                return "json";
            }
            case ParameterEditorMode.xml: {
                return "xml";
            }
            case ParameterEditorMode.yaml: {
                return "yaml";
            }
            case ParameterEditorMode.plaintext: {
                return "plaintext";
            }
            case ParameterEditorMode.text:
            case ParameterEditorMode.handlebars: {
                return "handlebars";
            }
            case ParameterEditorMode.markdown: {
                return "markdown";
            }
            default: {
                return "csharp";
            }
        }
    }

    const handleKeyPressByWritingClosingTag = (event: monaco.IKeyboardEvent) => {
        if (event.browserEvent.key === "/" && getLanguage() == "xml") {
            const editor = props.editorRef?.current;
            const currentModel = editor?.getModel() as monaco.editor.ITextModel;

            if (editor == null || currentModel == null) {
                return;
            }

            const position = editor.getPosition();
            if (position != null) {
                const lineRangeUntilCaret = new monaco.Range(position.lineNumber, 0, position.lineNumber, position.column - 2); // - 2 because we're now in the closing tag including the /, i.e. </^
                const lineRangeAfterCaret = new monaco.Range(position.lineNumber, position.column, position.lineNumber, currentModel.getLineLength(position.lineNumber))

                // For finding the nearest tag before caret
                const lineContentUntilCaret = currentModel.getValueInRange(lineRangeUntilCaret);
                // For determining the kind of closing tag we need, i.e. "foo>" or "foo" depending on if the editor has already closed it or not. 
                const lineContentAfterCaret = currentModel.getValueInRange(lineRangeAfterCaret)

                if (lineContentUntilCaret.includes("<") && !lineContentUntilCaret.includes("/")) {
                    const nearestTagName = lineContentUntilCaret.substring(lineContentUntilCaret.lastIndexOf("<") + 1, lineContentUntilCaret.lastIndexOf(">"));
                    // Calculate the range which should be replaced by the close tag.
                    const closingTagRange = new monaco.Range(
                        lineRangeUntilCaret.endLineNumber,
                        lineRangeUntilCaret.endColumn + nearestTagName.length + 1,
                        lineRangeUntilCaret.endLineNumber,
                        position.column
                    );
                    const restOfText = currentModel.getValueInRange(closingTagRange);

                    // We have to check if the line has text after the closing angle in the first tag,
                    // because the end tags doesn't appear automatically except on new-lines 
                    const lineHasContentAfterTag = lineContentAfterCaret !== "";
                    currentModel.pushEditOperations([], [
                        {
                            range: closingTagRange,
                            text: lineHasContentAfterTag ? `${nearestTagName}>${restOfText}` : `${nearestTagName}${restOfText}`
                        }
                    ], () => null)
                    const positionInsideTag = new monaco.Position(position.lineNumber, position.column - 2)
                    editor.setPosition(positionInsideTag);
                }

            }
        }
    }

    React.useEffect(() => {
        languageProvider.setNewLang(getLanguage());
        return () => {
            if (!props.isDiff && props.editorRef.current) {
                props.editorRef.current.dispose()
            }
        }
    }, [])

    const isEditorFontLoaded = useFontFaceObserver([{ family: "CourierPrime-Regular" }]);
    React.useLayoutEffect(() => {
        props.monacoRef.current?.editor.remeasureFonts();
    }, [isEditorFontLoaded])

    React.useEffect(() => {
        if (!props.isDiff && props.editorRef.current) {
            const codeEditor = (props.editorRef.current as monaco.editor.ICodeEditor);
            codeEditor?.onKeyUp((e) => handleKeyPressByWritingClosingTag(e));
            codeEditor.onDidFocusEditorWidget(() => {
                languageProvider.setNewLang(getLanguage());
            })
        }
    }, [props.editorRef.current, props.mode]) // Set language provider language and custom autocompletes after editor mount and language mode change

    React.useEffect(() => {
        if (!props.isDiff && props.editorRef.current && props.wordWrap != null) {
            const codeEditor = (props.editorRef.current as monaco.editor.ICodeEditor);
            codeEditor.updateOptions({ wordWrap: props.wordWrap ? "on" : "off" })
        }
    }, [props.editorRef.current, props.wordWrap])




    return getLanguage();
}

interface IUseEditorProps {
    isDiff?: boolean | undefined | null;
    mode: ParameterEditorMode | undefined;
    wordWrap?: boolean;
    editorRef: React.MutableRefObject<monaco.editor.IEditor | undefined>;
    monacoRef: React.MutableRefObject<typeof monaco | undefined>;
}
